/**
 * PlpProductTile — wraps HecProductTileCms for PLP/search context.
 *
 * Bridges the existing quick view system (which works with ProductHitWithSwatches)
 * with the unified tile (which works with HecProduct). Holds the original SCAPI
 * hit data for quick view, passes converted HecProduct to the tile for rendering.
 */
import { memo, useState } from "react";

import {
  ProductQuickView,
  ProductQuickViewDialog,
} from "~/components/products/product-quick-view";
import { useTrackCioResultClick } from "~/hec/analytics/cio-listing-beacons";
import { withBadges } from "~/hec/components/fsa-funds/grid";
import type { HecBadge } from "~/hec/types/product";
import { productHitToHecProduct } from "~/hec/utils/product-adapters";
import type { ProductHitWithSwatches } from "~/lib/app.types";
import { cn } from "~/lib/ui";

import { HecProductTileCms } from "./product-tile";

interface PlpProductTileProps {
  product: ProductHitWithSwatches;
  className?: string;
  gtmListId?: string;
  gtmListName?: string;
  gtmIndex?: number;
  /** COM-158: first-row (above-the-fold) tiles eager-load their image so it
   *  becomes an LCP candidate. Default lazy. */
  imagePriority?: boolean;
  /** Replaces the product's own badges (FSA Spender's "N Added"); `[]` = none.
   *  Omitted = the product's own badges. */
  badges?: HecBadge[];
  /** Optional page-specific analytics hooks (FSA Spender's zmags events). */
  onQuickViewOpen?: () => void;
  onProductLinkClick?: (href: string) => void;
  onQuickViewAdded?: () => void;
}

const PlpProductTileComponent = ({
  product,
  className,
  gtmListId,
  gtmListName,
  gtmIndex,
  imagePriority,
  badges,
  onQuickViewOpen,
  onProductLinkClick,
  onQuickViewAdded,
}: PlpProductTileProps) => {
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  // Tile stepper qty at QV open — pre-fills the buy form (SFCC-11825).
  const [quickViewQty, setQuickViewQty] = useState<number | undefined>();
  const converted = productHitToHecProduct(product);
  const hecProduct = badges ? withBadges(converted, badges) : converted;

  // SFCC-11906: fire the CIO browse_result_click / search_result_click beacon
  // when the shopper clicks through to the PDP. No-ops outside a CIO listing
  // (context null) or before hydration.
  const trackResultClick = useTrackCioResultClick();

  return (
    // data-view-item-id (= CIO productId) lets the PLP's viewport impression
    // observer (useTileImpressions, SFCC-11798) fire view_item_list only once a
    // tile actually scrolls into view.
    <div
      className={cn("flex flex-col", className)}
      data-view-item-id={product.productId}
      // Capture-phase so the beacon (navigator.sendBeacon, survives unload) is
      // enqueued before the link navigates. Gated to the product link so
      // Add-to-Cart / Quick View (buttons, not anchors) don't count as clicks.
      onClickCapture={e => {
        const target = e.target as HTMLElement;
        const anchor = target.closest("a[href]");
        const href = anchor?.getAttribute("href") ?? "";
        if (!href.includes(".html")) {
          return;
        }
        // The tile's own links only (COM-658). The Quick View dialog is a React
        // child, so its links (View Full Details, brand) bubble here too, but it
        // is portalled out of this div; legacy Fastr never tracked links inside
        // its Quick View (prod check 2026-10-28). The CIO beacon below keeps
        // counting them, as it did before.
        if (e.currentTarget.contains(target)) onProductLinkClick?.(href);
        trackResultClick({
          itemId: product.productId,
          itemName: product.productName,
          position: gtmIndex,
        });
      }}
    >
      <HecProductTileCms
        product={hecProduct}
        enableAddToCart
        enableRatings
        enableMessaging
        // "intent", not "viewport" (reverts SFCC-11297's PLP choice): viewport
        // fired a PDP .data loader for EVERY tile scrolled into view — ~36
        // Worker requests per PLP scroll-through against a 1-2 tile
        // click-through, multiplying PDP loader concurrency during traffic
        // spikes (isolate-OOM class).
        prefetch="intent"
        imagePriority={imagePriority}
        onOpenQuickView={quantity => {
          setQuickViewQty(quantity);
          setQuickViewOpen(true);
          onQuickViewOpen?.();
        }}
        gtmListId={gtmListId}
        gtmListName={gtmListName}
        gtmIndex={gtmIndex}
      />

      <ProductQuickViewDialog
        open={quickViewOpen}
        onClose={() => setQuickViewOpen(false)}
      >
        <ProductQuickView
          product={product}
          initialQuantity={quickViewQty}
          onClose={() => setQuickViewOpen(false)}
          // Tile-snapshot opt-in: PLP / search / hybrid grid tiles are the
          // in-scope quick-view surfaces (`?from=grid` fence on the product
          // resource). Recs carousels + favorites deliberately don't pass this
          // and stay on the live path.
          snapshotSource="grid"
          onAdded={onQuickViewAdded}
        />
      </ProductQuickViewDialog>
    </div>
  );
};
PlpProductTileComponent.displayName = "PlpProductTileComponent";

export const PlpProductTile = memo(
  PlpProductTileComponent,
  (prev, next) =>
    prev.product.productId === next.product.productId &&
    prev.className === next.className &&
    prev.imagePriority === next.imagePriority &&
    badgeText(prev.badges) === badgeText(next.badges),
);
PlpProductTile.displayName = "PlpProductTile";

// Text, not reference: callers build a fresh badges array every render.
const badgeText = (badges?: HecBadge[]) => badges?.map(b => b.text).join("|");
